import { cva, VariantProps } from 'class-variance-authority';
import { BiCheckCircle, BiCircle, BiErrorCircle, BiInfoCircle, BiXCircle } from 'react-icons/bi';
import { CgClose } from 'react-icons/cg';
import { toast, ToastT } from 'sonner';
import { cn } from '../../../utils/helpers';

const toastClasses = cva(
  [
    'py-[14px] px-5',
    'overflow-hidden rounded-md',
    'bg-white',
    'w-[415px] max-h-[72px] rounded-md overflow-hidden truncate relative border-l-[8px]',
    'border-r-white',
    'border-t-white',
    'border-b-white',
    'shadow-[0px_2px_6px_0px_rgba(39,46,53,0.04)]',
  ],
  {
    variants: {
      type: {
        success: ['border-l-positive'],
        error: ['border-l-negative'],
        info: ['border-l-positive'],
        warning: ['border-l-warning-base'],
        default: ['border-l-disabled'],
      },
    },
    defaultVariants: {
      type: 'default',
    },
  },
);

const iconClasses = cva(['w-7 h-7'], {
  variants: {
    type: {
      success: ['border-l-positive', 'text-positive'],
      error: ['border-l-negative', 'text-negative'],
      info: ['border-l-positive', 'text-positive'],
      warning: ['border-l-warning-base', 'text-warning-base'],
      default: ['border-l-disabled', 'text-disabled'],
    },
  },
});

export type ToastProps = VariantProps<typeof toastClasses> & {
  message: string;
  title: string;
  onUndo?: () => void;
  id: string | number;
  type: 'success' | 'error' | 'info' | 'warning' | 'default';
};

const callToast = (
  message = '',
  title = 'Success',
  position: ToastT['position'] = 'top-right',
  type: ToastProps['type'] = 'success',
) => {
  toast.custom((t) => <Toast title={title} message={message} type={type} id={t} />, { position });
};

export const toastSuccess = (message = '', title = 'Success', position: ToastT['position'] = 'top-right') =>
  callToast(message, title, position, 'success');
export const toastError = (message = '', title = 'Error', position: ToastT['position'] = 'top-right') =>
  callToast(message, title, position, 'error');
export const toastInfo = (message = '', title = 'Info', position: ToastT['position'] = 'top-right') =>
  callToast(message, title, position, 'info');
export const toastWarning = (message = '', title = 'Warning', position: ToastT['position'] = 'top-right') =>
  callToast(message, title, position, 'warning');
export const toastDefault = (message = '', title = 'Default', position: ToastT['position'] = 'top-right') =>
  callToast(message, title, position, 'default');

const Toast: React.FC<ToastProps> = ({ message, type, title, onUndo, id }) => {
  const toastIconMap: { [key in ToastProps['type']]: JSX.Element } = {
    success: <BiCheckCircle className={cn(iconClasses({ type }))} />,
    error: <BiXCircle className={cn(iconClasses({ type }))} />,
    info: <BiInfoCircle className={cn(iconClasses({ type }))} />,
    warning: <BiErrorCircle className={cn(iconClasses({ type }))} />,
    default: <BiCircle className={cn(iconClasses({ type }))} />,
  };

  return (
    <div className={cn(toastClasses({ type }))}>
      <div className="flex leading-[22px] justify-between pr-5 tracking-[0.56px] text-sm w-full">
        <div className="flex items-center gap-[14px]">
          <div className="flex-1">{toastIconMap[type]}</div>
          <div className="max-w-[276px] flex-1">
            <div className="font-semibold text-title">{title}</div>
            <div className="truncate text-content">{message}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onUndo && (
            <button onClick={() => onUndo()} type="button" className={cn('font-bold text-xs tracking-[0.48px]')}>
              UNDO
            </button>
          )}
          <CgClose
            role="button"
            onClick={() => toast.dismiss(id)}
            className={cn('w-5 h-5', 'flex-1 justify-self-end')}
          />
        </div>
      </div>
    </div>
  );
};

export default Toast;
